import { createFileRoute, Link } from "@tanstack/react-router";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import {
  Settings, ShieldCheck, Trash2, LayoutDashboard, Check, Loader2,
  Fingerprint, Scale, UserRound, Rocket, TrendingUp, Building2, Newspaper, Radar,
  CreditCard, ExternalLink, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useSubscription } from "@/hooks/useSubscription";
import { getPaddleEnvironment } from "@/lib/paddle";
import { cancelMySubscription, getCustomerPortalUrl } from "@/lib/payments/payments.functions";
import { useWorkspace } from "@/lib/workspace/useWorkspace";
import { WORKSPACES, WORKSPACE_IDS, type WorkspaceId } from "@/lib/workspace/workspaces";

const ICONS: Record<string, typeof Radar> = {
  Fingerprint, Scale, UserRound, Rocket, TrendingUp, Building2, Newspaper, ShieldCheck, Radar,
};

export const Route = createFileRoute("/settings")({
  head: () => ({ meta: [{ title: "Settings · CARIO INTEL" }] }),
  component: SettingsPage,
});

function SettingsPage() {
  const { id: workspaceId, workspace, setWorkspace, saving } = useWorkspace();
  const choose = async (id: WorkspaceId) => {
    if (id === workspaceId) return;
    await setWorkspace(id);
    toast.success(`Dashboard switched to ${WORKSPACES[id].label}`);
  };
  return (
    <AppLayout>
    <ModuleShell eyebrow="Settings" title="Platform controls" description="Operational preferences, compliance guardrails, and local workspace controls.">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Workspace" value={workspace.label.toUpperCase()} tone="cyan" />
        <StatTile label="Audit logging" value="ON" tone="success" />
        <StatTile label="Legal scope" value="OSINT" tone="success" />
        <StatTile label="Evidence integrity" value="ON" tone="cyan" />
      </div>

      <BillingPanel />

      <div className="glass rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          <LayoutDashboard className="h-4 w-4" /> Dashboard use case
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Pick how you work. The command surface, scoring language, default target type, modules and investigation playbooks all adapt to your choice.
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {WORKSPACE_IDS.map((id) => {
            const w = WORKSPACES[id];
            const Icon = ICONS[w.icon] ?? Radar;
            const active = id === workspaceId;
            return (
              <button
                key={id}
                onClick={() => choose(id)}
                disabled={saving}
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${
                  active ? "border-cyan/60 bg-cyan/10" : "border-panel-border bg-background/40 hover:bg-panel/60"
                }`}
              >
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-md ring-1"
                  style={{ background: `color-mix(in oklab, ${w.accent} 14%, transparent)`, ["--tw-ring-color" as string]: w.accent, borderColor: w.accent }}
                >
                  <Icon className="h-4 w-4" style={{ color: w.accent }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium">{w.label}</span>
                    {active && <Check className="h-3.5 w-3.5 text-cyan" />}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{w.blurb}</div>
                  <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {w.playbooks.length} playbooks · {w.tabs.length} modules
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan"><ShieldCheck className="h-4 w-4" /> Compliance</div>
          <div className="mt-4 space-y-3 text-sm text-muted-foreground">
            <p>CARIO INTEL is configured for lawful, publicly accessible OSINT workflows only.</p>
            <p>Evidence mutations are logged in the tamper-evident custody chain. Report exports append audit events.</p>
            <button
              onClick={() => {
                if (!window.confirm("Purge all local demo data from this browser?")) return;
                localStorage.removeItem("cario.intel.v1");
                toast.success("Local demo data purged");
              }}
              className="inline-flex items-center gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger hover:bg-danger/20"
            >
              <Trash2 className="h-4 w-4" /> Purge local demo data
            </button>
          </div>
        </div>
      </div>
    </ModuleShell>
    </AppLayout>
  );
}
function BillingPanel() {
  const env = getPaddleEnvironment();
  const sub = useSubscription();
  const cancel = useServerFn(cancelMySubscription);
  const portal = useServerFn(getCustomerPortalUrl);
  const [busy, setBusy] = useState<"cancel" | "portal" | null>(null);

  const statusLabel = sub.loading
    ? "LOADING"
    : sub.isActive
      ? (sub.status === "trialing" ? "TRIAL" : "ACTIVE")
      : (sub.status ?? "NONE").toUpperCase();

  async function openPortal() {
    setBusy("portal");
    try {
      const { url } = await portal({ data: { environment: env } });
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open the billing portal");
    } finally {
      setBusy(null);
    }
  }

  async function doCancel() {
    if (!window.confirm("Cancel your subscription now? All future payments stop immediately and premium console modules lock until you resubscribe.")) return;
    setBusy("cancel");
    try {
      await cancel({ data: { environment: env } });
      await sub.refresh();
      toast.success("Subscription canceled. Access is now locked until you resubscribe.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Cancellation failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
        <CreditCard className="h-4 w-4" /> Billing & plan
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <StatTile label="Plan" value={sub.tier?.name?.toUpperCase() ?? "NONE"} tone={sub.isActive ? "cyan" : "amber"} />
        <StatTile label="Status" value={statusLabel} tone={sub.isActive ? "success" : "danger"} />
        <StatTile
          label={sub.status === "trialing" ? "Trial ends" : "Renews"}
          value={sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : "—"}
        />
      </div>

      {sub.usage && sub.tier && (
        <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
          <div>Scans: {sub.usage.scans_used} / {sub.tier.scansPerMonth === -1 ? "∞" : sub.tier.scansPerMonth}</div>
          <div>AI messages: {sub.usage.ai_queries_used} / {sub.tier.aiQueriesPerMonth === -1 ? "∞" : sub.tier.aiQueriesPerMonth}</div>
          <div>Dossiers: {sub.usage.dossiers_used} / {sub.tier.dossiersPerMonth === -1 ? "∞" : sub.tier.dossiersPerMonth}</div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 rounded-md border border-cyan/40 bg-cyan/10 px-3 py-2 text-sm text-cyan hover:bg-cyan/20"
        >
          {sub.isActive ? "Change plan" : "Start free trial"}
        </Link>
        {sub.productId && (
          <button
            onClick={openPortal}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-md border border-panel-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-60"
          >
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            Manage payment method & invoices
          </button>
        )}
        {sub.productId && sub.status !== "canceled" && (
          <button
            onClick={doCancel}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger hover:bg-danger/20 disabled:opacity-60"
          >
            {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            Cancel subscription
          </button>
        )}

      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Cancelling stops all future charges at our merchant of record straight away — no further renewals or trial conversions — and premium features lock immediately. Your Vault cases are preserved and unlock again when you resubscribe.
      </p>
    </div>
  );
}
